// src/components/CoachPostImageField.js — admin: the image part of the
// Coach Post editor (Notifications → Coach Posts). Upload from the device
// (button, drag-and-drop, or paste a screenshot), pick from the image library,
// or paste a link; choose Small / Medium / Large; preview exactly what members
// see on a phone or a computer (same CoachPostImage component as the Dashboard).
// Design: docs/ux/coach-post-image-preview.html.

import React, { useEffect, useRef, useState } from 'react';
import CoachPostImage, { normalizePostImageSize } from './CoachPostImage';
import { prepareImage, uploadPostImage, fetchPostImageLibrary } from '../services/postImageUpload';

const C = {
    charcoal: 'var(--mom-charcoal)',
    // Production sets warm-gray == charcoal and cream-dark == cream, so soft
    // tones and hairlines are derived (see reference_live_theme_values).
    soft: 'color-mix(in srgb, var(--mom-charcoal) 70%, var(--mom-cream))',
    faint: 'color-mix(in srgb, var(--mom-charcoal) 50%, var(--mom-cream))',
    line: 'color-mix(in srgb, var(--mom-charcoal) 13%, var(--mom-cream))',
    well: 'color-mix(in srgb, var(--mom-cream) 55%, #fff)',
    track: 'color-mix(in srgb, var(--mom-charcoal) 7%, var(--mom-cream))',
    danger: '#b4443c',
};
const sans = 'var(--mom-font-sans)';

const SIZE_HINT = {
    small: 'Small — a quick visual beside your words.',
    medium: 'Medium — a good fit for most screenshots.',
    large: 'Large — best for a wide photo or banner.',
};

const ERROR_TEXT = {
    'not-configured': "Uploading isn't switched on yet — the storage key still needs adding. You can choose from the library or paste a link meanwhile.",
    decode: "This picture can't be opened in this browser (often an iPhone HEIC photo on a computer). Upload it from your phone, or save it as JPEG or PNG first.",
    'too-large': 'That file is too large. Please choose one under 40 MB.',
    'not-image': "That file isn't an image.",
    'not-an-image': "That file isn't an image.",
    encode: "Couldn't prepare that picture. Please try another.",
    'storage-failed': "Upload didn't go through. Please try again.",
    network: "Upload didn't go through — check your connection and try again.",
};

function fileNameFromUrl(url) {
    try { return decodeURIComponent(new URL(url).pathname.split('/').pop()) || 'Image'; }
    catch { return 'Image'; }
}

function Btn({ children, onClick, dark, disabled, small }) {
    const [hover, setHover] = useState(false);
    return (
        <button type="button" onClick={onClick} disabled={disabled}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: small ? '7px 12px' : '10px 16px', borderRadius: 11,
                fontFamily: sans, fontSize: small ? 12.5 : 13, fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
                border: `1px solid ${dark ? 'var(--mom-charcoal)' : C.line}`,
                background: dark ? 'var(--mom-charcoal)' : (hover && !disabled ? C.well : '#fff'),
                color: dark ? '#fff' : C.charcoal,
                boxShadow: dark && hover && !disabled ? '0 4px 14px rgba(62,47,47,0.22)' : 'none',
                transition: 'background 0.15s, box-shadow 0.15s',
            }}>
            {children}
        </button>
    );
}

function Segmented({ value, options, onChange, compact }) {
    return (
        <div role="radiogroup" style={{ display: 'inline-flex', background: C.track, borderRadius: compact ? 10 : 12, padding: compact ? 3 : 4, gap: 2 }}>
            {options.map((o) => {
                const on = o.value === value;
                return (
                    <button key={o.value} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.value)}
                        style={{
                            fontFamily: sans, fontSize: compact ? 11.5 : 13, fontWeight: 600,
                            padding: compact ? '6px 12px' : '8px 18px', borderRadius: compact ? 8 : 9, border: 'none', cursor: 'pointer',
                            background: on ? '#fff' : 'transparent', color: on ? C.charcoal : C.soft,
                            boxShadow: on ? '0 1px 4px rgba(0,0,0,0.10)' : 'none', transition: 'all 0.15s',
                        }}>{o.label}</button>
                );
            })}
        </div>
    );
}

function LibraryTile({ img, selected, onPick }) {
    const [hover, setHover] = useState(false);
    return (
        <button type="button" onClick={() => onPick(img.imageUrl)} title={img.title || ''}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            style={{
                padding: 0, cursor: 'pointer', borderRadius: 10, overflow: 'hidden', aspectRatio: '1',
                border: selected ? '2px solid var(--mom-primary)' : `1px solid ${C.line}`,
                transform: hover ? 'translateY(-1px)' : 'none', boxShadow: hover ? '0 4px 12px rgba(0,0,0,0.10)' : 'none',
                transition: 'transform 0.15s, box-shadow 0.15s', background: '#fff',
            }}>
            <img src={img.thumbnailUrl} alt="" loading="lazy" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </button>
    );
}

// Paste-a-screenshot goes to ONE field: the most recently opened editor.
// Coach Posts and Announcements share the Notifications page, and with both
// editors open a single ⌘V would otherwise upload into both.
const pasteOwners = [];

function Spinner() {
    return <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.5)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.9s linear infinite' }} />;
}

// Optional (all absent ⇒ the Coach Post editor, unchanged):
//   renderPreview({ size, device }) — the host draws its own true-to-life
//     preview inside the shared Phone/Computer frame (Announcements use it).
//   previewVisible — when to show that preview (default: body or image set).
//   sizeHints — per-size helper lines, for hosts whose sizes read differently.
function CoachPostImageField({ imageUrl, imageSize, onChange, onBusyChange, getToken, body, coachAvatarUrl, renderPreview, previewVisible, sizeHints }) {
    const size = normalizePostImageSize(imageSize);
    const fileRef = useRef(null);
    const [mode, setMode] = useState(null);           // null | 'library' | 'link'
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(null); // { previewUrl } while sending
    const [pending, setPending] = useState(null);     // prepared blob kept for Retry
    const [error, setError] = useState('');
    const [library, setLibrary] = useState(null);     // null = not loaded
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [linkDraft, setLinkDraft] = useState('');
    const [linkError, setLinkError] = useState('');
    const [device, setDevice] = useState('phone');
    const [dims, setDims] = useState(null);
    const [isTouch] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches);

    useEffect(() => { onBusyChange?.(!!uploading); }, [uploading, onBusyChange]);

    // Describe the chosen image's shape ("Tall screenshot", "Wide photo"…).
    useEffect(() => {
        setDims(null);
        if (!imageUrl) return undefined;
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setDims({ w: img.naturalWidth, h: img.naturalHeight }); };
        img.referrerPolicy = 'no-referrer';
        img.src = imageUrl;
        return () => { cancelled = true; };
    }, [imageUrl]);

    const send = async (prepared) => {
        setError('');
        setUploading({ previewUrl: prepared.previewUrl });
        const r = await uploadPostImage(prepared.blob, getToken);
        setUploading(null);
        if (r.url) {
            setPending(null);
            setMode(null);
            URL.revokeObjectURL(prepared.previewUrl);
            onChange({ imageUrl: r.url, imageSize: size });
        } else {
            setPending(prepared);
            setError(r.error);
        }
    };

    const takeFile = async (file) => {
        if (!file || uploading) return;
        setError(''); setPending(null);
        try { await send(await prepareImage(file)); }
        catch (e) { setError(e.message in ERROR_TEXT ? e.message : 'decode'); }
    };

    // Paste a screenshot (⌘V / Ctrl+V) anywhere while the editor is open —
    // only when the clipboard holds an image and no text, so pasting words into
    // the message box is never hijacked.
    const takeFileRef = useRef(takeFile);
    takeFileRef.current = takeFile;
    useEffect(() => {
        const token = {};
        pasteOwners.push(token);
        const onPaste = (e) => {
            if (pasteOwners[pasteOwners.length - 1] !== token) return;
            const dt = e.clipboardData;
            if (!dt || dt.getData('text/plain')) return;
            const item = Array.from(dt.items || []).find((i) => i.kind === 'file' && i.type.startsWith('image/'));
            if (!item) return;
            e.preventDefault();
            takeFileRef.current(item.getAsFile());
        };
        window.addEventListener('paste', onPaste);
        return () => {
            window.removeEventListener('paste', onPaste);
            const i = pasteOwners.indexOf(token);
            if (i >= 0) pasteOwners.splice(i, 1);
        };
    }, []);

    const openLibrary = async () => {
        setMode('library'); setError('');
        if (library || libraryLoading) return;
        setLibraryLoading(true);
        const imgs = await fetchPostImageLibrary().catch(() => []);
        setLibrary(imgs || []);
        setLibraryLoading(false);
    };

    const applyLink = () => {
        const v = linkDraft.trim();
        if (!/^https:\/\/\S+$/i.test(v)) { setLinkError('Please paste a full link starting with https://'); return; }
        setLinkError(''); setLinkDraft(''); setMode(null);
        onChange({ imageUrl: v, imageSize: size });
    };

    const remove = () => { setMode(null); setError(''); setPending(null); onChange({ imageUrl: '', imageSize: size }); };

    const onDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const file = Array.from(e.dataTransfer?.files || []).find((f) => f.type.startsWith('image/')) || e.dataTransfer?.files?.[0];
        takeFile(file);
    };

    const shape = !dims ? '' : dims.h > dims.w * 1.25 ? 'Tall image' : dims.w > dims.h * 1.25 ? 'Wide image' : 'Square-ish image';

    const choices = (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Btn dark onClick={() => { if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); } }} disabled={!!uploading}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4" /><path d="M7 9l5-5 5 5" /><path d="M4 20h16" /></svg>
                Upload from this device
            </Btn>
            <Btn onClick={openLibrary} disabled={!!uploading}>Choose from library</Btn>
            <Btn onClick={() => { setMode('link'); setError(''); }} disabled={!!uploading}>Paste a link</Btn>
        </div>
    );

    return (
        <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => takeFile(e.target.files?.[0])} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <span style={{ fontFamily: sans, fontSize: 12.5, fontWeight: 700, color: C.charcoal }}>Image</span>
                <span style={{ fontFamily: sans, fontSize: 12, color: C.faint }}>optional</span>
            </div>

            {/* Uploading */}
            {uploading && (
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', border: `1px solid ${C.line}`, borderRadius: 14, padding: 12, background: C.well }}>
                    <img src={uploading.previewUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `1px solid ${C.line}`, opacity: 0.7 }} />
                    <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: C.charcoal }}>Uploading…</p>
                        <p style={{ fontFamily: sans, fontSize: 12, color: C.soft }}>Making it light and fast for members.</p>
                    </div>
                    <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--mom-charcoal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner /></span>
                </div>
            )}

            {/* Empty — drop zone */}
            {!uploading && !imageUrl && (
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    style={{
                        border: `1.5px dashed ${dragOver ? 'var(--mom-primary)' : 'color-mix(in srgb, var(--mom-charcoal) 22%, var(--mom-cream))'}`,
                        background: dragOver ? 'color-mix(in srgb, var(--mom-primary) 6%, #fff)' : C.well,
                        borderRadius: 14, padding: '22px 18px', textAlign: 'center', transition: 'all 0.15s',
                    }}>
                    <p style={{ fontFamily: sans, fontSize: 14, fontWeight: 600, color: C.charcoal, marginBottom: 4 }}>{dragOver ? 'Drop to add it' : 'Add an image'}</p>
                    <p style={{ fontFamily: sans, fontSize: 12.5, color: C.soft, marginBottom: 14 }}>
                        Screenshots and photos both work — you'll choose the size next.
                        {!isTouch && ' Drag one here, or paste a screenshot (⌘V / Ctrl+V).'}
                    </p>
                    {choices}
                </div>
            )}

            {/* Chosen */}
            {!uploading && imageUrl && (
                <>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center', border: `1px solid ${C.line}`, borderRadius: 14, padding: 12, background: C.well, flexWrap: 'wrap' }}>
                        <img src={imageUrl} alt="" referrerPolicy="no-referrer" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `1px solid ${C.line}`, background: '#fff' }} />
                        <div style={{ flex: 1, minWidth: 140 }}>
                            <p style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: C.charcoal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileNameFromUrl(imageUrl)}</p>
                            <p style={{ fontFamily: sans, fontSize: 12, color: C.soft }}>{shape ? `${shape} · ` : ''}shown whole, never cropped</p>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <Btn small onClick={() => setMode(mode ? null : 'replace')}>Replace</Btn>
                            <Btn small onClick={remove}>Remove</Btn>
                        </div>
                    </div>
                    {mode === 'replace' && <div style={{ marginTop: 12 }}>{choices}</div>}
                    <div style={{ marginTop: 14 }}>
                        <Segmented value={size} onChange={(s) => onChange({ imageUrl, imageSize: s })}
                            options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]} />
                        <p style={{ fontFamily: sans, fontSize: 12, color: C.soft, marginTop: 8 }}>{(sizeHints || SIZE_HINT)[size]}</p>
                    </div>
                </>
            )}

            {/* Error (with Retry when the picture is ready to resend) */}
            {error && !uploading && (
                <div role="alert" style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <p style={{ fontFamily: sans, fontSize: 12.5, color: C.danger, flex: 1, minWidth: 200, lineHeight: 1.5 }}>{ERROR_TEXT[error] || ERROR_TEXT['storage-failed']}</p>
                    {pending && error !== 'not-configured' && <Btn small onClick={() => send(pending)}>Try again</Btn>}
                </div>
            )}

            {/* Library */}
            {mode === 'library' && !uploading && (
                <div style={{ marginTop: 12, border: `1px solid ${C.line}`, borderRadius: 14, padding: 14, background: '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ fontFamily: sans, fontSize: 12.5, fontWeight: 700, color: C.charcoal }}>Your image library</span>
                        <Btn small onClick={() => setMode(imageUrl ? 'replace' : null)}>Close</Btn>
                    </div>
                    {libraryLoading && <p style={{ fontFamily: sans, fontSize: 12.5, color: C.soft }}>Loading…</p>}
                    {!libraryLoading && library && library.length === 0 && (
                        <p style={{ fontFamily: sans, fontSize: 12.5, color: C.soft }}>No images yet — upload one from this device instead.</p>
                    )}
                    {!libraryLoading && library && library.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(76px, 1fr))', gap: 8, maxHeight: 260, overflowY: 'auto', padding: 2 }}>
                            {library.map((img) => (
                                <LibraryTile key={img.id || img.imageUrl} img={img} selected={img.imageUrl === imageUrl}
                                    onPick={(url) => { setMode(null); onChange({ imageUrl: url, imageSize: size }); }} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Paste a link */}
            {mode === 'link' && !uploading && (
                <div style={{ marginTop: 12, border: `1px solid ${C.line}`, borderRadius: 14, padding: 14, background: '#fff' }}>
                    <span style={{ fontFamily: sans, fontSize: 12.5, fontWeight: 700, color: C.charcoal, display: 'block', marginBottom: 8 }}>Paste an image link</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input className="mom-input" autoFocus value={linkDraft} placeholder="https://…"
                            onChange={(e) => { setLinkDraft(e.target.value); setLinkError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') setMode(imageUrl ? 'replace' : null); }}
                            style={{ fontSize: 13, flex: 1 }} />
                        <Btn dark onClick={applyLink}>Use</Btn>
                        <Btn onClick={() => setMode(imageUrl ? 'replace' : null)}>Cancel</Btn>
                    </div>
                    {linkError && <p style={{ fontFamily: sans, fontSize: 12, color: C.danger, marginTop: 6 }}>{linkError}</p>}
                </div>
            )}

            {/* Preview — what members see */}
            {(previewVisible !== undefined ? previewVisible : (body?.trim() || imageUrl)) && (
                <div style={{ marginTop: 20, borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.faint }}>Preview — what members see</span>
                        <Segmented compact value={device} onChange={setDevice} options={[{ value: 'phone', label: 'Phone' }, { value: 'desk', label: 'Computer' }]} />
                    </div>
                    {renderPreview ? renderPreview({ size, device }) : (
                    <div style={{ background: 'linear-gradient(160deg, color-mix(in srgb, var(--mom-primary) 9%, var(--mom-cream)), var(--mom-cream) 45%, color-mix(in srgb, var(--mom-charcoal) 4%, var(--mom-cream)))', borderRadius: 16, padding: 16, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
                        <div style={{ width: device === 'phone' ? 330 : 680, maxWidth: '100%', transition: 'width 0.25s ease' }}>
                            <div style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 20, padding: device === 'phone' ? 18 : 24, boxShadow: 'var(--shadow-sm)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: coachAvatarUrl ? 'transparent' : 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {coachAvatarUrl
                                            ? <img src={coachAvatarUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <span style={{ fontFamily: sans, fontSize: 13, fontWeight: 700, color: '#fff' }}>★</span>}
                                    </div>
                                    <span style={{ fontFamily: sans, fontSize: 14, fontWeight: 600, color: C.charcoal }}>Danielle</span>
                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--mom-primary)' }} />
                                    <span style={{ fontFamily: sans, fontSize: 12, color: C.soft }}>Just now</span>
                                </div>
                                {body?.trim() && (
                                    <p style={{ fontFamily: sans, fontSize: device === 'phone' ? 14.5 : 15, color: C.charcoal, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{body.trim()}</p>
                                )}
                                {imageUrl && (
                                    <div style={{ marginTop: body?.trim() ? 16 : 0 }}>
                                        <CoachPostImage src={imageUrl} size={size} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CoachPostImageField;
